import type { CarReader } from '@ipld/car'
import type { Applog, PromiseType } from '@wovin/core'
import type { DefaultFalse, DefaultTrue } from '@wovin/utils/types'
import type { IconName } from '@wovin/ui-solid'
import type { Accessor, Component, JSX, ParentComponent, Resource } from 'solid-js'
import type { retrievePubDataWithExtras } from '../ipfs/store-sync'
import type { DivProps } from '../ui/utils-ui'
import { createTimer } from '@solid-primitives/timer'
import { A } from '@solidjs/router'
import { decodePubFromCar, isEncryptedApplog, queryAndMap, querySingleAndMap, ThreadInMemory } from '@wovin/core'
import { Iconify, IconifyButton, IconifyNames as IconifyNamesShared } from '@wovin/ui-solid'
import { Logger } from 'besonders-logger'
import classNames from 'classnames'
import { partition, size } from 'lodash-es'
import { createEffect, createMemo, createResource, createSignal, For, mergeProps, onMount, Show, splitProps, Suspense } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { initialized, setStorageError } from '../appInit'
import { useAgent } from '../data/agent/AgentState'
import { copyToClipboard } from '../data/block-ui-helpers-paste'
import { BlockVM } from '../data/VMs/BlockVM'
import { retrievePubToThread } from '../ipfs/retrievePubToThread'
import { getPubExtraInfo } from '../ipfs/store-sync'
import { DefaultCenter, DefaultFull, DefaultSmall } from '../types/unocss-utils'
import { getStorageIssues, isOnline, isStorageReachable } from '../ui/online-second'
import { useRawThread } from '../ui/reactive'
import {
	createAsyncButtonHandler,
	createAsyncMemo,
	explorerUrl,
	isColorDark,
	makeGlobalHover,
	mergeStyles,
	stopPropagation,
	stringToColor,
	useLocationNavigate,
} from '../ui/utils-ui'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO)

export const ShortID: Component<{ id: string, href?: string }> = (props) => {
	return (
		<Dynamic
			component={props.href ? A : 'span'}
			class={props.href ? 'a-unstyled underline' : ''}
			flex='inline items-center'
			href={props.href ?? undefined}
			target={props.href ? '_blank' : undefined}
		>
			{/* max-w-5 truncate */}
			<code title={props.id}>{props.id.slice(-7)}</code>
		</Dynamic>
	)
}

export const CopyableID: Component<{ id: string, label?: string, buttonSize?: string }> = (_props) => {
	const props = mergeProps({ buttonSize: '5' }, _props)
	return (
		<span flex='inline items-center' onClick={stopPropagation} my--2>
			<code p='y-2 r--1'>{props.id.slice(-7)}</code>
			{' '}
			{/* t-[2px]  */}
			<wa-copy-button
				class='copy-button-compact'
				value={props.id}
				copy-label={props.label ?? undefined}
			>
				<Iconify name='copy-light' size={props.buttonSize} slot='copy-icon' />
			</wa-copy-button>
		</span>
	)
}

export const CopyableSpan: ParentComponent<{ value: string, class?: string }> = (props) => {
	const [ourProps, otherProps] = splitProps(props, ['value', 'class', 'children'])
	const [justCopied, setJustCopied] = createSignal(false)

	const handleCopy = (e: MouseEvent) => {
		const selection = window.getSelection()
		if (selection && selection.toString().length > 0) {
			return // User is selecting text, don't copy
		}
		copyToClipboard(ourProps.value)
		setJustCopied(true)
		setTimeout(() => setJustCopied(false), 600)
	}

	return (
		<span class='relative inline-block'>
			<span
				class={classNames('cursor-pointer select-text', ourProps.class)}
				onClick={handleCopy}
				{...otherProps}
			>
				{ourProps.children}
			</span>
			<Show when={justCopied()}>
				<div class='pointer-events-none absolute left-1/2 top-1/2 rounded bg-black/70 px-1 py-0.5 color-white font-medium leading-none -translate-x-1/2 -translate-y-1/2'>
					Copied!
				</div>
			</Show>
		</span>
	)
}
export const Spinner: ParentComponent<{ size?: string, color?: string }> = (props) => {
	return (
		<div flex='~ col items-center justify-center w-full h-full'>
			<wa-spinner
				style={`${props.size ? `font-size: ${props.size}; ` : ''}${
					props.color ? `--indicator-color: ${props.color}; --track-color: var(--un-preset-theme-colors-basebg);` : ''
				}`}
			/>
			{props.children}
		</div>
	)
}
export const DynamicColored: ParentComponent<
	{
		text: string
	} & JSX.HTMLAttributes<HTMLSpanElement>
> = (props) => {
	const [ourProps, otherProps] = splitProps(props, ['text', 'children'])
	const color = createMemo(() => stringToColor(ourProps.text))

	return (
		<span
			{...otherProps}
			style={mergeStyles(otherProps.style, {
				'color': isColorDark(color()) ? 'white' : 'black',
				'background-color': color(),
			})}
		>
			{ourProps.children || ourProps.text}
		</span>
	)
}

export const [/* _idHover */, useIdHover] = makeGlobalHover((active: boolean, myID: boolean) => ({
	'data-test': 123,
	'class': classNames(
		'transition-duration-300 transition-property-[opacity]',
		(active || myID) ? 'opacity-100' : 'opacity-40',
		myID && 'font-bold underline',
	),
}))
// TODO: useChangeHighlight (?)
export const HighlightSameOnHover: ParentComponent<
	{
		id: string
	} & JSX.HTMLAttributes<HTMLSpanElement>
> = (props) => {
	const [ourProps, otherProps] = splitProps(props, ['id', 'children'])
	return (
		<span
			{...((useIdHover('?', ourProps.id) as any))}
			{...otherProps}
		>
			{ourProps.children}
		</span>
	)
}

export { Iconify } from '@wovin/ui-solid'
export const IconifyNames = IconifyNamesShared
// Re-export shared types for backwards compatibility
export type { IconName } from '@wovin/ui-solid'


export const ResourceSpinner: ParentComponent<{ resource: Resource<any>, spinner?: JSX.Element }> = (props) => {
	const errorMessage = createMemo(() => props.resource.error?.message || '')
	createEffect(() => {
		const msg = errorMessage()
		if (msg) ERROR(`ResourceSpinner error:`, msg)
	}) // otherwise, we just render but don't log
	return (
		<Suspense
			fallback={props.spinner ?? <Spinner />}
		>
			<Show
				when={!errorMessage()}
				fallback={(
					<span color-yellow>
						<div class='i-ph:warning-bold' />
						{errorMessage()}
					</span>
				)}
			>
				{props.children}
			</Show>
		</Suspense>
	)
}
export const KidCount: Component<{ block: string }> = (props) => {
	const rawDS = useRawThread()
	const [kidCount] = createDeferredResource(() => props.block, async (block) => {
		// if (Math.random() > 0.5) throw new Error(`TEST err`)
		// for (let index = 0; index < 1000000; index++) {
		// 	Math.random()
		// 	// DEBUG(`T`)
		// }
		return (BlockVM.get(block, rawDS) as any).recursiveKidCount
	})
	return (
		<ResourceSpinner resource={kidCount}>
			<span>{kidCount()}</span>
		</ResourceSpinner>
	)
}

export async function getPersistenceStory() {
	// https://storage.spec.whatwg.org/#persistence
	// new situation apparently
	if ((navigator as any).storageBuckets) { // chrome only ?
		const inboxBucket = await (navigator as any).storageBuckets.open('note3keys', {
			durability: 'strict', // Or `'relaxed'`.
			persisted: true, // Or `false`.
		})
	}
	const wasPersisted = await navigator.storage.persisted()
	const persistGranted = await navigator.storage.persist()
	const estimate = await navigator.storage.estimate()
	WARN(`Persisted storage status:`, { persistGranted, wasPersisted, estimate })
	return { persistGranted, wasPersisted }
}
export async function askForStoragePerms() {
	const isChromium = !!(window as any).chrome // https://stackoverflow.com/a/62797156/
	if (isChromium) {
		VERBOSE.force('try grant notification')
		const notificationGrantHack = await Notification.requestPermission() // https://stackoverflow.com/a/53573849/2919380
		DEBUG.force('notificationGrantHack result', notificationGrantHack) // will be string 'granted' upon request
	}
	const persistStillNotGranted = !(await navigator.storage.persist())
	if (persistStillNotGranted) {
		getPersistenceStory()
		window.location.reload()
	} else {
		DEBUG('Storage permissions acquired :)')
		setStorageError(null)
	}
}
export const StoragePermsErr: Component<{
	bold: boolean
	[x: string]: any // TODO keyof typeof Iconify props somehow
}> = (fullProps) => {
	const isChromium = !!(window as any).chrome // https://stackoverflow.com/a/62797156/
	return (
		<div flex='~ col'>
			<div>
				Persistent storage permission is not granted, your data might not persist.
				<br />
				<Show when={isChromium}>
					In chromium, there is no way to ask for this permission. 🙄
					<br />
					<a target='_blank' href='https://stackoverflow.com/a/53573849/2919380'>Awkwardly enough</a>
					, Notification Permissions are the best way to (maybe) get persistent storage, so:
				</Show>
				<Show when={!isChromium}>
					You need to grant the permission in the site settings in your browser.
				</Show>
			</div>
			<wa-button size='s' mt-3 self-end onClick={askForStoragePerms}>
				Click to {isChromium ? 'request Notification' : 'check for'} permissions
			</wa-button>
		</div>
	)
}

type CloudIcon = 'cloud-check' | 'cloud-slash' | 'cloud-check-bold' | 'cloud-slash-bold' | 'cloud-warning' | 'cloud-warning-bold'
export const Online: Component<{
	bold: boolean
	[x: string]: any // TODO keyof typeof Iconify props somehow
}> = (fullProps) => {
	const [props, otherProps] = splitProps(fullProps, ['bold'])
	const iconName = createMemo(() => {
		const bold = props.bold ? '-bold' : ''
		const warningOrCheck = !isStorageReachable() ? '-warning' : '-check'
		DEBUG({ bold, warning: warningOrCheck })

		return (isOnline()
			? `cloud${warningOrCheck}${bold}`
			: `cloud-slash${bold}`) as CloudIcon
	})

	const tt = createAsyncMemo(async () => {
		if (isOnline()) {
			if (isStorageReachable()) {
				return 'All good'
			} else {
				if (!initialized()) return <span>online, but storage not reachable and app not initialized</span>
				const storageSituation = await getStorageIssues()
				LOG({ storageSituation })
				return (
					<div flex='~ col' gap-1>
						<span>online, but storage not reachable</span>
						<For each={Object.entries(storageSituation)}>
							{([eachURL, eachRes]) => (
								<wa-button variant={eachRes.ok ? 'success' : 'danger'}>
									<Iconify slot='start' name={eachRes.ok ? 'check-circle' : 'x-circle'} />
									{eachURL}
								</wa-button>
							)}
						</For>
					</div>
				) // TODO more specific feedback about failures
			}
		} else {
			return 'Offline: Check your device connection'
		}
	})
	return (
		<Show when={initialized() /* && (!isOnline() || !isStorageReachable()) */}>
			<wa-tooltip
				// hoist
				cursor-pointer
				trigger='click hover'
				placement='bottom'
			>
				<Iconify onclick={async () => LOG(await getStorageIssues())} {...otherProps} name={iconName()} />
				<div slot='content'>
					{tt()}
				</div>
			</wa-tooltip>
		</Show>
	)
}

export function createDeferredResource<R, S>(
	source: S | false | null | (() => S | false | null),
	fetcher: (arg: S) => Promise<R>,
	{ deferOnUpdate }: { deferOnUpdate?: boolean } = {},
) {
	let loadedOnce = false // ? this was a preemptive measure, not sure if needed
	return createResource(source, async (source) => {
		// await new Promise<void>(resolve => queueMicrotask(resolve)) // (i) https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide#timeout_and_microtask_example
		DEBUG(`[createDeferredResource] loadedOnce=${loadedOnce}, deferOnUpdate=${deferOnUpdate}`)
		if (!loadedOnce || deferOnUpdate) {
			await new Promise<void>(resolve => setTimeout(resolve)) // i.e. defer
		}
		loadedOnce = true
		// await new Promise<void>(resolve => setTimeout(resolve, 1000)) // for testing

		return fetcher(source)
		// const result = await fetcher(source)
		// DEBUG(`[createDeferredResource] result:`, result)
		// return result
	})
}
// export function createDeferredMemo<R>(func: () => R) {
// 	const [val, setVal] = createSignal<R>(null)
// 	setTimeout(() => {
// 		// await new Promise<void>(resolve => queueMicrotask(resolve)) // (i) https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide#timeout_and_microtask_example
// 		await new Promise<void>(resolve => setTimeout(resolve)) // i.e. defer
// 		// await new Promise<void>(resolve => setTimeout(resolve, 1000)) // for testing
// 		return func()
// 	})
// 	return val
// }

export const DeleteWithConfirm: Component<
	DivProps & {
		handler: () => Promise<void> | void
		text?: string
		confirmText?: string
		confirmButtonLabel?: string
		buttonVariant?: string
		icon?: IconName
		certaintySeconds?: number
	}
> = (allProps) => {
	const [props, restProps] = splitProps(allProps, [
		'text',
		'handler',
		'confirmText',
		'certaintySeconds',
		'buttonVariant',
		'icon',
		'confirmButtonLabel',
	])

	const [opened, setOpened] = createSignal(false)
	const [openedSecs, setOpenedSecs] = createSignal(0)
	createTimer(() => setOpenedSecs(s => s + 1), () => opened() && 1000, setInterval)
	createEffect(() => !opened() && setOpenedSecs(0))

	const deleteButtonProps = createAsyncButtonHandler(props.handler)

	return (
		<wa-dropdown
			// onClick={stopPropagation}
			placement='bottom-start'
			distance={3}
			{...restProps}
			onwa-show={() => setOpened(true)}
			onwa-hide={() => setOpened(false)}
		>
			<IconifyButton
				slot='trigger'
				variant={props.buttonVariant || 'danger'}
				size='s'
				icon={props.icon || 'trash-duotone'}
				text={props.text}
			/>
			<div class='darkdropdown outline-rounded-white p-4 line-height-5' flex='~ col gap-4'>
				<div whitespace-pre-line>{props.confirmText ?? 'You sure bout\' dat?'}</div>
				<wa-button
					class=''
					slot='trigger'
					size='s'
					variant={props.buttonVariant || 'danger'}
					{...deleteButtonProps()}
					disabled={props.certaintySeconds && openedSecs() < props.certaintySeconds}
				>
					<div w-4 h-4 class='i-ph:trash-duotone' />
					{props.certaintySeconds && openedSecs() < props.certaintySeconds
						? `Think ${(props.certaintySeconds - openedSecs())}s`
						: (props.confirmButtonLabel || 'Yes, Delete')}
				</wa-button>
			</div>
		</wa-dropdown>
	)
}

export const DeleteWithConfirmDialog: Component<
	DivProps & {
		handler: () => Promise<void> | void
		label?: string
		dialogText?: string | Element
		dialogLabel?: string
		certaintySeconds?: number
	}
> = (allProps) => {
	const [props, restProps] = splitProps(allProps, ['label', 'handler', 'dialogText', 'certaintySeconds', 'dialogLabel'])

	const [opened, setOpened] = createSignal(false)
	const [openedSecs, setOpenedSecs] = createSignal(0)
	createTimer(() => setOpenedSecs(s => s + 1), () => opened() && 1000, setInterval)
	createEffect(() => !opened() && setOpenedSecs(0))

	const deleteButtonProps = createAsyncButtonHandler(props.handler)

	let dialogRef
	return (
		<>
			<wa-button size='s' variant='danger' onClick={() => dialogRef?.show()}>
				<div w-4 h-4 class='i-ph:trash-duotone' slot='start' />
				{props.label}
			</wa-button>
			<wa-dialog
				{...restProps}
				ref={dialogRef}
				label={props.dialogLabel ?? 'Really delete?'}
				onwa-show={() => setOpened(true)}
				onwa-hide={() => setOpened(false)}
			>
				<div flex='~ col gap-4'>
					<div whitespace-pre-line>{props.dialogText ?? 'You sure bout\' dat?'}</div>
					<wa-button
						slot='footer'
						variant='danger'
						{...deleteButtonProps()}
						disabled={props.certaintySeconds && openedSecs() < props.certaintySeconds}
					>
						<div w-4 h-4 class='i-ph:trash-duotone' />
						{props.certaintySeconds && openedSecs() < props.certaintySeconds
							? `Think ${(props.certaintySeconds - openedSecs())}s`
							: 'Yes, Delete'}
					</wa-button>
				</div>
			</wa-dialog>
		</>
	)
}

export const PubInfoBox: Component<{ pubID?: string | null, pinCID?: string, label?: string, carFile?: CarReader }> = (props) => {
	const appThread = useRawThread()

	const [fetchedPub] = createDeferredResource(
		() => ({ pubID: props.pubID, pinCID: props.pinCID, carFile: props.carFile }),
		async ({ pubID, pinCID, carFile }) => /* : Promise<Partial<ReturnType<typeof retrievePubDataWithExtras>>> */ {
			DEBUG(`PubInfoBox.fetch`, { pubID, pinCID })
			if (!pubID && !pinCID && !carFile) return null
			// if (matchingSubOrPub()) return null
			// throw new Error(`TEST pubinfo err`)
			if (carFile) {
				const { applogs, info } = await decodePubFromCar(carFile)
				// TODO: display INFO
				const [encryptedLogs, plainLogs] = partition(applogs, isEncryptedApplog)
				const thread = ThreadInMemory.fromArray((plainLogs as any) as Applog[], `preview-carfile`)
				return {
					info,
					thread,
					encryptedCount: encryptedLogs.length,
					...getPubExtraInfo(appThread, thread),
				}
			} else {
				const thread = ThreadInMemory.empty(`pubInfo-${pinCID || pubID}`)
				const { cid, info } = await retrievePubToThread(thread, pubID, { pinCID }, appThread)
				return {
					info,
					thread,
					cid,
					// encryptedCount,
					...getPubExtraInfo(appThread, thread),
				}
			}
		},
	)

	return (
		<ResourceSpinner resource={fetchedPub}>
			<Show when={fetchedPub()}>
				<div p-2 border='1 solid lightblue-700' rounded gap-2>
					<PubInfos pub={fetchedPub() as any} label={props.label} {...({} as any)} />
				</div>
			</Show>
		</ResourceSpinner>
	)
}

export const PubInfos: Component<{
	pub: PromiseType<ReturnType<typeof retrievePubDataWithExtras>>
}> = (props) => {
	const agent = useAgent()
	const info = createMemo(() => props.pub.info)
	// const encryptedApplogCount = info().maybeEncryptedApplogs.filter(log => !log?.ag).length // encrypted logs are byte arrays, so they have no .ag prop // HACKish
	// const encryptedFor= filterAndMap(lastWriteWins(info().thread), { en: publicationNameString, at: 'pub/encryptedFor' }, 'vl') // no need for a filter just doing a find below instead
	// if (sharedKeyLog) encryptedFor = sharedKeyLog.en
	// TODO check agents for if we know their agent string. have any atoms from them, and have a public derivation key from them
	return (
		<Show when={info()}>
			{/* HACK: should use proper reactivity */}
			{(info) => {
				const infoLogs = createMemo(() => info().logs)
				const encryptedFor = queryAndMap(infoLogs(), { at: 'pub/sharedKey' }, 'en')
				const pubName = querySingleAndMap(infoLogs(), { at: 'pub/name' }, 'vl') // TODO: editable :)
				const pubBy = querySingleAndMap(infoLogs(), { at: 'pub/name' }, 'en') // TODO: editable :)
				const sharedKeyLog: Applog = agent.getSharedKeyApplogForCurrentAgent(infoLogs())
				DEBUG({ sharedKeyLog, encryptedFor })
				const isEncryptedForMe = createMemo(() => encryptedFor.includes(agent.ag))
				const encryptedForWithoutMe = createMemo(() =>
					encryptedFor.filter(eachEn => eachEn !== agent.ag)
						.map(eachOtherEn => [eachOtherEn, `${agent.getKnownAgents().get()?.get(eachOtherEn)?.agString || 'unknown'}`]),
				)
				return (
					<>
						<Show when={pubName}>
							<KeyValueDiv label='Name' class='text-sm'>
								{pubName}
							</KeyValueDiv>
						</Show>
						<Show when={(encryptedFor as any).size}>
							<KeyValueDiv label='Encrypted' class='text-xs'>
								{encryptedFor.toString()}
							</KeyValueDiv>
						</Show>
						<KeyValueDiv label='Agents' class='text-xs'>
							<span class='grow-1 overflow-hidden text-ellipsis whitespace-nowrap'>{props.pub.agents.join(', ') /* TODO sharedKeyLogs  */}</span>
						</KeyValueDiv>
						<KeyValueDiv label='Timerange' class='text-xs'>
							<Show when={props.pub.thread.size} fallback='Empty Thread'>
								<wa-relative-time date={props.pub.thread.firstLog?.ts} />
								➜
								<wa-relative-time date={props.pub.thread.latestLog?.ts} />
							</Show>
						</KeyValueDiv>
						<KeyValueDiv label='CID' class='text-xs'>
							<a
								href={explorerUrl(props.pub.cid.toString())}
								target='_blank'
								overflow-hidden
								text-ellipsis
								class='color-inherit visited:color-inherit'
							>
								<code>{props.pub.cid.toString()}</code>
							</a>
						</KeyValueDiv>
						<Show when={props.pub.counterInfo?.counter !== undefined}>
							<KeyValueDiv label='Snapshot #' class='text-xs'>
								<code>#{props.pub.counterInfo.counter}</code>
							</KeyValueDiv>
						</Show>
						<KeyValueDiv label='Content' class='text-xs'>
							{`${props.pub.thread.size} Applogs `}
							<span class={props.pub.newLogs ? 'color-blue' : 'color-green'}>
								({props.pub.newLogs ? `of which ${props.pub.newLogs} are not in the local data` : `all exist in your local data`})
							</span>
							<br />
							<Show when={props.pub.encryptedCount}>
								<div>
									encrypted logs for: {isEncryptedForMe()
										? (
												<wa-tooltip placement='right' style={{ '--max-width': '300px' }}>
													{` you & ${encryptedForWithoutMe().length} others`}
													<div slot='content' min-w-max w-max>
														<div flex='~ col' class='items-start'>
															<For each={encryptedForWithoutMe()}>
																{(agentInfo, _index) => <div text-left>{agentInfo[0]}:{agentInfo[1]}</div>}
															</For>
														</div>
													</div>
												</wa-tooltip>
											)
										: ' NOT you: '}
								</div>
							</Show>
						</KeyValueDiv>
						<KeyValueDiv
							label='Content'
							class={classNames(
								'text-xs',
								props.pub.entityOverlapCount && props.pub.newLogs ? 'color-yellow' : '',
							)}
						>
							Related to
							{' '}
							<strong>{props.pub.entityOverlapCount} entities</strong>
							{' '}
							in your local data
						</KeyValueDiv>
					</>
				)
			}}
		</Show>
	)
}

export const NameValueBadge: ParentComponent<{ name: string, variant?: string }> = (props) => {
	return (
		<wa-badge variant={props.variant ?? 'neutral'}>
			<span>
				<strong>{props.name}:</strong>
				{' '}
				{props.children}
			</span>
		</wa-badge>
	)
}
export const KeyValueDiv: ParentComponent<DivProps & { label: string }> = (allProps) => {
	const [props, restProps] = splitProps(allProps, ['label', 'children'])
	return (
		<div flex='~ items-center gap-2' {...restProps}>
			<strong flex='shrink-0 grow-0'>
				{props.label}:
			</strong>
			{props.children}
		</div>
	)
}

export const ButtonGroup: Component<
	DivProps & {
		size?: typeof DefaultSmall
		buttonData: any
	}
> = (allProps) => {
	const [props, topProps] = splitProps(allProps, ['size', 'buttonData'])
	// const { size = DefaultSmall } = props // ! only include non reactive props
	return (
		<wa-button-group size={size} {...topProps}>
			<For each={props.buttonData}>
				{({ restProps, icon, click }) => (
					<wa-button
						{...restProps}
						onClick={click}
						size={props.size ?? DefaultSmall}
					>
						<Iconify name={icon} />
					</wa-button>
				)}
			</For>
		</wa-button-group>
	)
}
export const AgentButton: Component<
	DivProps & {
		size?: typeof DefaultSmall
		buttonData: any
	}
> = (allProps) => {
	const [props, topProps] = splitProps(allProps, ['size', 'buttonData'])
	// const { size = DefaultSmall } = props // ! only include non reactive props
	const { restProps, icon, click, text, title } = props.buttonData
	return (
		<wa-button
			{...restProps}
			onClick={click}
			alt={title}
			title={title}
			aria-label={title}
			size={props.size ?? DefaultSmall}
		>
			<Show when={!!icon}>
				<Iconify name={icon} />
			</Show>
			{text}
		</wa-button>
	)
}

export const NumberBadge: Component<
	DivProps & {
		num: Accessor<number>
		variant?: string
		pulse?: DefaultFalse
		pill?: DefaultTrue
	}
> = (allProps) => {
	const [props, restProps] = splitProps(allProps, ['num', 'variant', 'pulse', 'pill'])
	return (
		<wa-badge {...restProps} variant={props.variant ?? 'primary'} pill={props.pill ?? true} pulse={props.pulse ?? false}>
			{props.num()}
		</wa-badge>
	)
}

export { IconifyButton }
export type { IconifyButtonProps } from '@wovin/ui-solid'

export const FlexBetween: ParentComponent<
	JSX.HTMLAttributes<HTMLDivElement> & {
		w?: typeof DefaultFull
		items?: typeof DefaultCenter
	}
> = (allProps) => {
	// const { children, items = DefaultCenter, w = DefaultFull, class: classes, ...restProps } = props
	const [props, restProps] = splitProps(allProps, ['w', 'children', 'items', 'class'])
	return (
		<div
			flex='~ justify-between'
			class={`${props.class} items-${props.items ?? DefaultCenter} w-${props.w ?? DefaultFull}`}
			{...restProps}
		>
			{props.children}
		</div>
	)
}

export const FileSelect: ParentComponent<{ onFile: (file: FileList) => any }> = (props) => {
	let fileInput: HTMLInputElement

	function onFileChange(this: HTMLInputElement, event) {
		event.preventDefault()
		DEBUG(`[FileSelect] onFileChange`, { self: this, event })
		props.onFile(this.files)
	}
	onMount(() => {
		fileInput.addEventListener('change', onFileChange, false)
		return () => fileInput?.removeEventListener('change', onFileChange, false)
	})
	return (
		<div>
			{/* <strong>{props.children}:</strong>&nbsp; */}
			<input type='file' ref={fileInput} hidden />
			<wa-button size='s' onClick={() => fileInput.click()}>
				{props.children}
			</wa-button>
		</div>
	)
}
export const RedirectWorkaround: ParentComponent<
	DivProps & {
		url: string
		replace?: boolean
	}
> = (props) => {
	const [ourProps, otherProps] = splitProps(props, ['url'])
	const { locnav, location, navigate } = useLocationNavigate()

	// createEffect(()=> { if (isPreviewOrSearch()) {
	queueMicrotask(() => navigate(props.url, { replace: props.replace ?? true }))
	// 	}})

	return (
		<div
			{...otherProps}
		>
			Redirecting to
			{' '}
			<a href={ourProps.url}>
				<code>{ourProps.url}</code>
			</a>
			{props.children}
		</div>
	)
}
